/** Presentation helpers shared by the timeline's components. */
import { DELIVERY_METHODS } from '@/api/notifications';

// Family names live in the model layer — the label derivation needs them too.
export { familyName } from './model/labels';
import type { Origin, TimelineNotification, WarningCode } from './model/types';

/** One-letter family badges. Short enough for a dense row, unambiguous in a
 *  tooltip. Unknown families fall back to their first letter. */
const FAMILY_BADGES: Record<string, string> = {
  tour_participant: 'P',
  order: 'O',
  departure: 'D',
  user_departure: 'UD',
  tour_departure: 'TD',
  booking: 'B',
  user: 'U',
  tour_operator: 'TO',
  beat: 'I',
};

export function familyBadge(family: string): string {
  return FAMILY_BADGES[family] ?? (family.slice(0, 1).toUpperCase() || '?');
}

/** Per-channel hues. Distinct at 8px, and readable on the light surface. */
export const METHOD_COLORS: Record<number, string> = {
  [DELIVERY_METHODS.EMAIL]: '#2f6fd0',
  [DELIVERY_METHODS.SMS]: '#1f9d63',
  [DELIVERY_METHODS.CHAT]: '#8659c5',
  [DELIVERY_METHODS.PUSH_NOTIFICATION]: '#d98324',
  [DELIVERY_METHODS.SENTRY]: '#8a8f98',
  [DELIVERY_METHODS.SNS_TOPIC]: '#8a8f98',
  [DELIVERY_METHODS.HTTP_REQUEST]: '#5a6673',
  [DELIVERY_METHODS.WHATSAPP]: '#128c7e',
  [DELIVERY_METHODS.AIRBNB]: '#e0546c',
};

export function methodColor(method: number | null): string {
  return method === null ? '#8a8f98' : (METHOD_COLORS[method] ?? '#5a6673');
}

export const ORIGIN_LABELS: Record<Origin, string> = {
  own: 'Own',
  inherited: 'Inherited',
  system: 'System',
};

export const ORIGIN_HELP: Record<Origin, string> = {
  own: 'Defined by this tenant.',
  inherited:
    "Defined by the franchise parent. It reaches this tenant's guests, but can only be edited on the parent.",
  system: 'Platform-wide notification with no tour operator.',
};

export const TOUR_OVERRIDE_GROUPS: { mode: number; label: string; help: string }[] = [
  { mode: 0, label: 'Always', help: 'Sent regardless of any tour-specific override.' },
  { mode: 1, label: 'Default', help: 'Sent unless a tour-specific override exists.' },
  { mode: 2, label: 'Tour-specific', help: 'Only for the listed tours.' },
];

export const WARNING_LABELS: Record<WarningCode, string> = {
  disabled: 'Disabled',
  no_delivery_methods: 'No channels',
  no_enabled_delivery_methods: 'All channels off',
  suppressed_by_child: 'Overridden by this tenant',
  event_cannot_fire: 'Event never fires',
  unknown_event_code: 'Unknown event',
  never_fired: 'No sends in 30d',
};

export const WARNING_HELP: Record<WarningCode, string> = {
  disabled: 'The notification is switched off.',
  no_delivery_methods: 'No delivery method is configured, so nothing can be sent.',
  no_enabled_delivery_methods: 'Every configured delivery method is switched off.',
  suppressed_by_child:
    'This parent notification is overridable and the tenant defines its own on the same event, so delivery skips it.',
  event_cannot_fire:
    'No event with this code is ever emitted, so this notification can never be delivered.',
  unknown_event_code: 'This event code is not in the catalog.',
  never_fired: 'Enabled, but it has not sent anything in the last 30 days.',
};

/** Warnings that mean "this will not reach a guest" rather than "look at me". */
const BLOCKING: ReadonlySet<WarningCode> = new Set<WarningCode>([
  'no_delivery_methods',
  'no_enabled_delivery_methods',
  'suppressed_by_child',
  'event_cannot_fire',
]);

export function warningSeverity(code: WarningCode): 'error' | 'warning' | 'info' {
  if (BLOCKING.has(code)) return 'error';
  if (code === 'never_fired') return 'warning';
  return 'info';
}

/** Would this notification actually deliver something today? */
export function isLive(nf: TimelineNotification): boolean {
  if (nf.enabled !== true) return false;
  if (nf.suppressedByChild) return false;
  if (nf.canFire === false) return false;
  // undefined = channels undecided (a proposal) — not a reason to call it dead.
  if (nf.deliveryMethods !== undefined && !nf.deliveryMethods.some((d) => d.enabled !== false)) {
    return false;
  }
  return true;
}

export function formatFires(fires: number | undefined): string {
  if (fires === undefined) return '';
  if (fires >= 1000) return `${Math.round(fires / 100) / 10}k`;
  return String(fires);
}

/** "2 days ago" / "just now" — coarse on purpose, this is an overview. */
export function relativeTime(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '';
  const seconds = Math.max(0, Math.round((now - then) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}
